import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Invoices from "@/pages/Invoices";
import InvoiceBuilder from "@/pages/InvoiceBuilder";
import PublicInvoice from "@/pages/PublicInvoice";
import Splits from "@/pages/Splits";
import Wallet from "@/pages/Wallet";
import Tax from "@/pages/Tax";
import Expenses from "@/pages/Expenses";
import Settings from "@/pages/Settings";
import Pricing from "@/pages/Pricing";
import CryptoCheckout from "@/pages/CryptoCheckout";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Refunds from "@/pages/Refunds";
import TaxSeasonPack from "@/pages/TaxSeasonPack";
import Filings from "@/pages/Filings";
import AdminFilings from "@/pages/AdminFilings";
import Referrals from "@/pages/Referrals";
import AdminReferrals from "@/pages/AdminReferrals";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Leaderboard from "@/pages/Leaderboard";
import AppLayout from "@/components/AppLayout";
import ErrorBoundary from "@/components/ErrorBoundary";

const Protected = ({ children }) => {
  const { user, loading, sessionExpired } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-zinc-400">
        <div className="text-xs uppercase tracking-[0.2em]">Loading</div>
      </div>
    );
  }
  if (!user) {
    return sessionExpired
      ? <Navigate to="/login" state={{ from: location, reason: "expired" }} replace />
      : <Navigate to="/" replace />;
  }
  return children;
};

/**
 * AppRouter — intercepts the Emergent OAuth return BEFORE any route match.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 *
 * Google Auth sends the user back to `${origin}/dashboard#session_id=...`.
 * We must consume that fragment BEFORE <Protected> runs its /auth/me check,
 * otherwise ProtectedRoute will redirect to `/` and the one-shot session_id
 * is lost. Reading via `useLocation().hash` keeps this reactive — once
 * AuthCallback calls `replaceState` the hash disappears and React re-renders
 * into the normal routes.
 */
const AppRouter = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/leaderboard" element={<Leaderboard />} />
      <Route path="/pay/:invoiceId" element={<PublicInvoice />} />
      <Route path="/public/invoice/:invoiceId" element={<PublicInvoice />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/refunds" element={<Refunds />} />
      <Route path="/tax-pack" element={<TaxSeasonPack />} />

      <Route element={<Protected><AppLayout /></Protected>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/invoices" element={<Invoices />} />
        <Route path="/invoices/new" element={<InvoiceBuilder />} />
        <Route path="/splits" element={<Splits />} />
        <Route path="/wallet" element={<Wallet />} />
        <Route path="/tax" element={<Tax />} />
        <Route path="/expenses" element={<Expenses />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/filings" element={<Filings />} />
        <Route path="/admin/filings" element={<AdminFilings />} />
        <Route path="/referrals" element={<Referrals />} />
        <Route path="/admin/referrals" element={<AdminReferrals />} />
        <Route path="/checkout/:subInvoiceId" element={<CryptoCheckout />} />
      </Route>
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster theme="dark" position="top-right" />
      </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
